/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * SavedAnalysesPanel.tsx
 *
 * A sidebar component dropped into the Market Analysis tab.
 * Appears to the right of the main market content on desktop,
 * below it on mobile.
 *
 * Three responsibilities:
 *   1. "Save current" — saves the active AI-generated report with a user-chosen name
 *   2. Saved analyses list — shows all prior saves, newest first
 *   3. Load — clicking a saved analysis calls onLoadAnalysis so the parent
 *              repopulates the main view with that content
 *
 * Token comes in as a prop (not sessionStorage/localStorage) matching
 * Dashboard.tsx's auth pattern.
 */

import React, { useEffect, useState, useCallback } from 'react';

interface SavedAnalysesPanelProps {
  token: string;
  currentReport: string;
  currentQuery: string;
  /** Parent sets its marketResult + msa state to the loaded content */
  onLoadAnalysis: (content: string, query: string) => void;
  dealId?: number;
}

interface AnalysisSummary {
  id: number;
  market_name: string;
  query: string;
  notes: string | null;
  created_at: string;
}

const API = 'https://coast-terminal-production.up.railway.app';

/** Converts an ISO date string into a human-readable relative label */
const relativeTime = (dateStr: string): string => {
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ── Small inline modal for naming a new save ─────────────────────────────────

interface SaveModalProps {
  defaultName: string;
  saving: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}

const SaveModal: React.FC<SaveModalProps> = ({ defaultName, saving, onSave, onCancel }) => {
  const [name, setName] = useState(defaultName);

  const gold = '#b8956a';
  const bg = '#ffffff';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(8,13,24,0.75)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ backgroundColor: '#ffffff', border: `1px solid ${borderLight}`, padding: '24px', width: '100%', maxWidth: '380px', boxShadow: '0 8px 40px rgba(13,61,92,0.15)' }}>
        <div style={{ fontSize: '13px', fontWeight: 800, color: textPrimary, marginBottom: '4px' }}>Save Analysis</div>
        <div style={{ fontSize: '11px', color: textMuted, marginBottom: '16px' }}>Give this analysis a name so you can find it later.</div>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && name.trim() && onSave(name.trim())}
          placeholder="e.g. Chicago Multifamily Q1 2026"
          style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#fafaf8', border: `1px solid ${borderLight}`, padding: '10px 14px', color: textPrimary, fontSize: '12px', outline: 'none', fontFamily: 'inherit', marginBottom: '14px' }}
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '9px', backgroundColor: 'transparent', border: `1px solid ${border}`, color: textSecondary, fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onSave(name.trim())}
            disabled={saving || !name.trim()}
            style={{ flex: 1, padding: '9px', backgroundColor: name.trim() && !saving ? gold : surfaceHigh, color: name.trim() && !saving ? bg : textMuted, border: 'none', fontSize: '11px', fontWeight: 800, cursor: name.trim() && !saving ? 'pointer' : 'default', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const SavedAnalysesPanel: React.FC<SavedAnalysesPanelProps> = ({
  token, currentReport, currentQuery, onLoadAnalysis, dealId
}) => {
  const gold = '#b8956a';
  const goldDim = 'rgba(201,169,110,0.1)';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const surfaceHigh = '#e6ddd4';
  const border = '#c4a870';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const red = '#CC2200';

  const headers = { Authorization: `Bearer ${token}` };

  const [analyses, setAnalyses] = useState<AnalysisSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // ── Fetch saved list ───────────────────────────────────────────────────────

  const fetchAnalyses = useCallback(async () => {
    try {
      const url = dealId
        ? `${API}/api/market-analyses/?deal_id=${dealId}`
        : `${API}/api/market-analyses/`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAnalyses(data.analyses);
    } catch {
      // Silent fail — this panel is supplementary, not critical
    } finally {
      setLoading(false);
    }
  }, [dealId]); // eslint-disable-line

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  // ── Save handler ───────────────────────────────────────────────────────────

  const handleSave = async (name: string) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/market-analyses/`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market_name: name,
          query: currentQuery,
          content: currentReport,
          deal_id: dealId || null,
          metadata: { saved_at: new Date().toISOString() },
        }),
      });
      if (!res.ok) throw new Error();
      await fetchAnalyses();
      setShowSaveModal(false);
      setToast('Analysis saved');
      setTimeout(() => setToast(null), 2500);
    } catch {
      setToast('Failed to save');
      setTimeout(() => setToast(null), 2500);
    } finally {
      setSaving(false);
    }
  };

  // ── Load handler ───────────────────────────────────────────────────────────

  const handleLoad = async (analysisId: number) => {
    if (loadingId === analysisId) return;
    setLoadingId(analysisId);
    try {
      const res = await fetch(`${API}/api/market-analyses/${analysisId}`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      // Pass the full report content + the original query back to the parent.
      // The parent sets marketResult and msa state, which re-renders the main view.
      onLoadAnalysis(data.content, data.query);
      setActiveId(analysisId);
    } catch {
      setToast('Failed to load');
      setTimeout(() => setToast(null), 2500);
    } finally {
      setLoadingId(null);
    }
  };

  // ── Delete handler ─────────────────────────────────────────────────────────

  const handleDelete = async (e: React.MouseEvent, analysisId: number) => {
    e.stopPropagation(); // Don't trigger the load handler
    if (!window.confirm('Delete this saved analysis?')) return;
    await fetch(`${API}/api/market-analyses/${analysisId}`, { method: 'DELETE', headers });
    setAnalyses(prev => prev.filter(a => a.id !== analysisId));
    if (activeId === analysisId) setActiveId(null);
  };

  // Generate a default save name from the current query (trimmed to 40 chars)
  const defaultName = currentQuery
    ? currentQuery.slice(0, 40) + (currentQuery.length > 40 ? '...' : '')
    : 'Market Analysis';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <div style={{ backgroundColor: '#ffffff', border: `1px solid ${border}`, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '400px' }}>

        {/* Panel header */}
        <div style={{ padding: '13px 16px 11px', borderBottom: `1px solid ${border}`, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 800, color: textSecondary, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Saved Analyses</div>
            <div style={{ fontSize: '9px', color: textMuted, marginTop: '1px' }}>{analyses.length} saved</div>
          </div>
          {/* Save button only visible when there's a report to save */}
          {currentReport && (
            <button
              onClick={() => setShowSaveModal(true)}
              style={{ fontSize: '9px', fontWeight: 800, color: gold, backgroundColor: goldDim, border: `1px solid rgba(201,169,110,0.3)`, padding: '5px 10px', cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}
            >
              + Save
            </button>
          )}
        </div>

        {/* Analyses list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px', color: textMuted, fontSize: '10px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Loading...
            </div>
          ) : analyses.length === 0 ? (
            <div style={{ padding: '28px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: textMuted, marginBottom: '4px' }}>No saved analyses yet.</div>
              <div style={{ fontSize: '10px', color: '#2A3A4C', lineHeight: 1.6 }}>Run a market analysis and click "+ Save" to save it here for later.</div>
            </div>
          ) : (
            <div>
              {analyses.map(analysis => (
                <div
                  key={analysis.id}
                  onClick={() => handleLoad(analysis.id)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${border}`,
                    cursor: 'pointer',
                    backgroundColor: activeId === analysis.id ? surfaceRaised : 'transparent',
                    borderLeft: `2px solid ${activeId === analysis.id ? gold : 'transparent'}`,
                    transition: 'background-color 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={e => { if (activeId !== analysis.id) (e.currentTarget as HTMLElement).style.backgroundColor = surfaceRaised; }}
                  onMouseLeave={e => { if (activeId !== analysis.id) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>
                        {analysis.market_name}
                      </div>
                      <div style={{ fontSize: '10px', color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>
                        {analysis.query.slice(0, 55)}{analysis.query.length > 55 ? '...' : ''}
                      </div>
                      <div style={{ fontSize: '9px', color: '#2A3A4C', letterSpacing: '0.5px' }}>
                        {relativeTime(analysis.created_at)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginTop: '1px' }}>
                      {/* Loading spinner for this item */}
                      {loadingId === analysis.id && (
                        <div style={{ width: '10px', height: '10px', border: `1px solid ${gold}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                      )}
                      {/* Active indicator */}
                      {activeId === analysis.id && loadingId !== analysis.id && (
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: gold }} />
                      )}
                      {/* Delete — always shown, small target */}
                      <button
                        onClick={(e) => handleDelete(e, analysis.id)}
                        title="Delete"
                        style={{ backgroundColor: 'transparent', border: 'none', color: textMuted, cursor: 'pointer', fontSize: '11px', padding: '2px 4px', lineHeight: 1 }}
                        onMouseEnter={e => (e.currentTarget.style.color = red)}
                        onMouseLeave={e => (e.currentTarget.style.color = textMuted)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Save modal */}
      {showSaveModal && (
        <SaveModal
          defaultName={defaultName}
          saving={saving}
          onSave={handleSave}
          onCancel={() => setShowSaveModal(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 300, padding: '10px 18px', backgroundColor: '#ffffff', border: `1px solid ${borderLight}`, color: textPrimary, fontSize: '12px', fontWeight: 700, boxShadow: '0 4px 24px rgba(13,61,92,0.10)' }}>
          {toast}
        </div>
      )}
    </>
  );
};

export default SavedAnalysesPanel;